import { useAppDispatch, useAppSelector } from "@/app/lib/features/redux/redux.hook";
import { useCallback, useMemo, useRef } from "react";
import { getCampaigns as getCampaignsThunk, getCampaign as getCampaignThunk } from "./campaign.thunk";
import { resetCampaigns, resetCampaign, setInitialCampaign } from "./campaign.slice";
import { isLoading, isError, isIdle, isSuccess } from "@/app/utils/asyncStatus";
import type { FilterRequest } from "../filters/filters.types";
import type { CampaignDetailData } from "./campaign.types";

const useCampaign = () => {
  const dispatch = useAppDispatch();
  const lastFiltersRef = useRef<Partial<FilterRequest> | undefined>(undefined);
  const state = useAppSelector((store) => store.campaign);

  // ==================== CAMPAIGNS LIST ====================
  const fetchCampaigns = useCallback(
    async (page: number = 1) => {
      const res = await dispatch(getCampaignsThunk({ page }));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchMoreCampaigns = useCallback(() => {
    if (!state.campaignsPagination) return;
    const { current_page, last_page } = state.campaignsPagination;
    if (current_page >= last_page) return;
    dispatch(getCampaignsThunk({ page: current_page + 1 }));
  }, [dispatch, state.campaignsPagination]);

  const clearCampaigns = useCallback(() => {
    dispatch(resetCampaigns());
  }, [dispatch]);

  const hasMoreCampaigns = useMemo(() => {
    if (!state.campaignsPagination) return false;
    return state.campaignsPagination.current_page < state.campaignsPagination.last_page;
  }, [state.campaignsPagination]);

  // ==================== CAMPAIGN DETAIL ====================
  const fetchCampaign = useCallback(
    async (code: string, filters?: Partial<FilterRequest>) => {
      lastFiltersRef.current = filters;
      const res = await dispatch(getCampaignThunk({ code, page: 1, filters }));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchMoreCampaignItems = useCallback(() => {
    if (!state.campaign || !state.pagination) return;

    const { current_page, last_page } = state.pagination;
    if (current_page >= last_page) return;

    dispatch(
      getCampaignThunk({
        code: state.campaign.code,
        page: current_page + 1,
        filters: lastFiltersRef.current,
      }),
    );
  }, [dispatch, state.campaign, state.pagination]);

  const clearCampaign = useCallback(() => {
    dispatch(resetCampaign());
  }, [dispatch]);

  /** SSR/ISR verisini Redux'a yükler (mount'ta bir kez) — bkz. setInitialCampaign. */
  const seedInitialCampaign = useCallback(
    (data: CampaignDetailData) => {
      dispatch(setInitialCampaign(data));
    },
    [dispatch],
  );

  const hasMoreCampaignItems = useMemo(() => {
    if (!state.pagination) return false;
    return state.pagination.current_page < state.pagination.last_page;
  }, [state.pagination]);

  return {
    ...state,

    // Campaigns list status
    isCampaignsIdle: isIdle(state.campaignsStatus),
    isCampaignsLoading: isLoading(state.campaignsStatus),
    isCampaignsError: isError(state.campaignsStatus),
    hasMoreCampaigns,

    // Campaign detail status
    isCampaignLoading: isLoading(state.campaignStatus),
    isCampaignSuccess: isSuccess(state.campaignStatus),
    isCampaignError: isError(state.campaignStatus),
    hasMoreCampaignItems,

    // Campaigns list actions
    fetchCampaigns,
    fetchMoreCampaigns,
    clearCampaigns,

    // Campaign detail actions
    fetchCampaign,
    fetchMoreCampaignItems,
    clearCampaign,
    seedInitialCampaign,
  };
};

export default useCampaign;
